import qs from 'qs';

import {
  IPhotoUploaderImage,
  ITransformation,
  ICrop,
} from '../MultiplePhotoUploader/multiplePhotoUploaderTypes';
import {
  Input_core_apimessages_ImageEntry,
} from '../../../gql/graphql';

const percentToDecimal = (percent: number): string => (percent / 100).toFixed(3);
const decimalToPercent = (decimal: string): number => parseFloat(decimal) * 100;

interface ICloudinaryTransformation {
  version?: string;
  quality?: string;
  flags?: string;
  angle?: string;
  format?: string;
  crop?: string;
  width?: number;
  height?: number;
  transformation?: ITransformation;
}

const LIMIT_SIZE_TRANSFORMATION = {
  crop: 'limit',
  width: 600,
  height: 600,
};

const THUMB_TRANSFORMATION = {
  crop: 'thumb',
  gravity: 'south',
  width: 96,
  height: 96,
  format: 'jpg',
};

/**
 * Returns a Reverb Core URL that redirects to a transformed version of the image
 * identified by `public_id` and `fileExtension`, transformed according to `options`.
 */
function transformedImageUrl(publicId: string, fileExtension: string, options: Record<string, any>) {
  const queryParams = {
    file_extension: fileExtension,
    options,
  };

  const queryString = qs.stringify(queryParams, { addQueryPrefix: true });

  return `/cloudinary/images/${publicId}${queryString}`;
}

// Convert our db representation of transformations (in decimals)
// to crop parameters in percentages
function parseCropFromTransformation(transform) {
  return {
    width: decimalToPercent(transform.width),
    height: decimalToPercent(transform.height),
    x: decimalToPercent(transform.x),
    y: decimalToPercent(transform.y),
  };
}

function baseTransformations(version: string): Partial<ICloudinaryTransformation> {
  return {
    version,
    quality: 'auto:eco',
    flags: 'progressive',
    angle: 'exif',
    format: 'jpg',
  };
}

class CloudinaryPhoto implements IPhotoUploaderImage {
  attrs: Input_core_apimessages_ImageEntry;

  id: string;

  publicId: string;

  version: string;

  width: number;

  height: number;

  transformation: ITransformation;

  crop: ICrop;

  angle: number;

  fileExtension: string;

  constructor(attrs: Input_core_apimessages_ImageEntry) {
    this.attrs = attrs;

    this.id = attrs.id || attrs.publicId;
    this.publicId = attrs.publicId;
    this.version = attrs.version;
    this.width = attrs.width;
    this.height = attrs.height;
    this.transformation = attrs.transformation || {};

    this.crop = parseCropFromTransformation(this.transformation);
    this.angle = this.transformation.angle;

    this.fileExtension = attrs.format;
  }

  copy() {
    return new CloudinaryPhoto(this.attrsWithUpdates);
  }

  get attrsWithUpdates() {
    return {
      ...this.attrs,
      transformation: this.userTransformation,
    };
  }

  get thumbUrl() {
    const transformations = {
      ...baseTransformations(this.version),
      ...THUMB_TRANSFORMATION,
      transformation: this.userTransformation,
    };

    return transformedImageUrl(this.publicId, this.fileExtension, transformations);
  }

  get fullUrl() {
    const transformations = {
      ...baseTransformations(this.version),
      ...LIMIT_SIZE_TRANSFORMATION,
      transformation: this.userTransformation,
    };

    return transformedImageUrl(this.publicId, this.fileExtension, transformations);
  }

  get fullUrlWithoutCrop() {
    const transformations = {
      ...baseTransformations(this.version),
      ...LIMIT_SIZE_TRANSFORMATION,
      transformation: this.angleTransformation,
    };

    return transformedImageUrl(this.publicId, this.fileExtension, transformations);
  }

  rotate() {
    this.angle = this.angle || 0;

    this.angle += 90;

    if (this.angle === 360) { this.angle = null; }

    // Because we've rotated the image, the crop coordinates
    // need to rotate as well.
    if (this.isCropped) {
      this.crop = {
        x: 100 - this.crop.height - this.crop.y,
        y: this.crop.x,
        width: this.crop.height,
        height: this.crop.width,
      };
    }
  }

  // helper methods

  get userTransformation() {
    return {
      ...this.cropTransformation,
      ...this.angleTransformation,
    };
  }

  get cropTransformation() {
    if (!this.isCropped) { return {}; }

    return {
      width: percentToDecimal(this.crop.width),
      height: percentToDecimal(this.crop.height),
      x: percentToDecimal(this.crop.x),
      y: percentToDecimal(this.crop.y),
      crop: 'crop',
    };
  }

  get angleTransformation() {
    if (!this.angle) { return {}; }

    return {
      angle: this.angle,
    };
  }

  get isCropped() {
    return this.crop.width && !this.isCroppedToFullImage;
  }

  get isCroppedToFullImage() {
    return this.crop.width === 100 && this.crop.height === 100;
  }
}

export { CloudinaryPhoto };
